import { useReactiveVar } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { dictionariesVar } from '../../../apollo/reactive-vars'
import {
  Energy,
  EnergyType,
  PrimaryEnergyFlow,
  Quadrant,
  checkForInvalid,
} from '../../../calculators/EnergyFlow'
import { Provider } from '../../../calculators/providers/common/Provider'
import { sortProvidersByPriority } from '../../../calculators/providers/util/provider-utility'
import { reduceToSumOfItems, sortByProperty } from '../../../util/array-utility'
import { getDemandEnergiesForCharts } from './chart-util'

export type EnergyWithName = Energy & {
  name: string
}

type EnergyWithNameAndProviderData = EnergyWithName & {
  provider: Provider
}

type ChartUtil = {
  energyTypes: EnergyType[]
  flowOptions: string[]
  flows: EnergyWithName[]
  defaultFlows: EnergyWithName[]
}

export type ChartProps = {
  titlePrefix: string
  chartUtil: ChartUtil
}

export const useChartUtil = (
  energyTypes: EnergyType[],
  demands?: PrimaryEnergyFlow[],
  providers?: Provider[],
): ChartUtil => {
  if (demands && providers) {
    throw new Error(
      'useChartUtil is not designed to work with both demands and providers.',
    )
  }

  const { t } = useTranslation()
  const { providerIdsToNames, demandIdsToNames } =
    useReactiveVar(dictionariesVar)

  const flows: EnergyWithName[] = []
  const defaultFlows: EnergyWithName[] = []

  function createMapEnergyTypeToSummedFlow<T extends Energy>(energyFlows: T[]) {
    return (energyType: EnergyType): EnergyWithName => ({
      name: t(`energyType.${energyType}`),
      energyType,
      hourSeries: energyFlows
        .filter((energy) => energy.energyType === energyType)
        .map((energy) => energy.hourSeries)
        .reduce(reduceToSumOfItems, []),
    })
  }

  if (demands) {
    const totalEnergiesPerEnergyType = energyTypes.map(
      createMapEnergyTypeToSummedFlow(demands),
    )

    flows.push(...totalEnergiesPerEnergyType)

    // Business:
    // default for local water grids: [Heating, Cooling]
    // default for local electrical grids: [Electrical]
    // default for buildings: [Heating, Cooling]
    const defaultsForDemandEnergies =
      energyTypes.length > 1
        ? totalEnergiesPerEnergyType.filter(
            (energy) => energy.energyType !== EnergyType.Electricity,
          )
        : totalEnergiesPerEnergyType

    defaultFlows.push(...defaultsForDemandEnergies)

    const translations = energyTypes.reduce(
      (idsToNames, energyType) => ({
        ...idsToNames,
        [energyType]: t(`energyType.${energyType}`),
      }),
      demandIdsToNames,
    )

    const demandEnergies = getDemandEnergiesForCharts(demands, translations)
    const sortedDemandEnergies = sortByProperty(demandEnergies, 'name')
    flows.push(...sortedDemandEnergies)
  }

  if (providers) {
    if (energyTypes.length !== 1) {
      throw new Error('Unknown error in chart utility')
    }

    const providerEnergyType = energyTypes[0]
    const filteredProviders = providers?.filter((provider) =>
      provider.providedEnergyTypes.includes(providerEnergyType),
    )

    const providerFlows = filteredProviders.map(
      (provider): EnergyWithNameAndProviderData => ({
        name: `${providerIdsToNames[provider.id]} ${t(
          'charts.suffix.provided',
        )}`,
        energyType: providerEnergyType,
        provider,
        hourSeries:
          provider.flows
            ?.filter(checkForInvalid)
            .filter((flow) => flow.quadrant === Quadrant.Provided)
            .filter((flow) => flow.energyType === providerEnergyType)
            .map((flow) => flow.hourSeries)
            .reduce(reduceToSumOfItems, []) ?? [],
      }),
    )
    const sortedProviderFlows: EnergyWithName[] = providerFlows
      .sort((a, b) => sortProvidersByPriority(a.provider, b.provider))
      .map(({ provider, ...energyProperties }) => energyProperties)

    const totalEnergiesPerEnergyType = energyTypes.map(
      createMapEnergyTypeToSummedFlow(providerFlows),
    )

    flows.push(...totalEnergiesPerEnergyType)
    defaultFlows.push(...totalEnergiesPerEnergyType)
    flows.push(...sortedProviderFlows)

    // Add requested input flows

    const singleEnergyTypeProviderInputFlows = filteredProviders
      .filter(({ providedEnergyTypes }) => providedEnergyTypes.length === 1)
      .map(
        (provider): EnergyWithNameAndProviderData => ({
          name: `${providerIdsToNames[provider.id]} ${t(
            'charts.suffix.requested',
          )}`,
          energyType: providerEnergyType,
          provider,
          hourSeries: provider.inputFlows?.find(Boolean)?.hourSeries ?? [],
        }),
      )

    const dualEnergyTypeProviderInputFlows = filteredProviders
      .filter(({ providedEnergyTypes }) => providedEnergyTypes.length > 1)
      .flatMap(
        (provider) =>
          provider.inputFlows?.map((flow) => ({
            name: `${providerIdsToNames[provider.id]} ${t(
              `charts.suffix.${flow.energyType}`,
            )}`,
            energyType: flow.energyType,
            provider,
            hourSeries: flow.hourSeries,
          })) ?? [],
      )

    const sortedInputFlows: EnergyWithName[] = [
      ...singleEnergyTypeProviderInputFlows,
      ...dualEnergyTypeProviderInputFlows,
    ]
      .sort((a, b) => sortProvidersByPriority(a.provider, b.provider))
      .map(({ provider, ...energyProperties }) => energyProperties)

    flows.push(...sortedInputFlows)
  }

  const flowOptions = flows.map((energy) => energy.name)

  return { energyTypes, flowOptions, flows, defaultFlows }
}
